import { useImperativeHandle, useMemo, useRef, useState } from "react";

import {
  DateTimeValidationError,
  PickerChangeHandlerContext,
} from "@mui/x-date-pickers";
import { Dayjs } from "dayjs";
import { useDidMount, useForceRerender } from "hooks";

import { UseDateTimePickerParams } from "./DateTimePicker.types";

export const useDateTimePicker = <R>({
  ref,
  defaultValue,
  value,
  onChange,
}: UseDateTimePickerParams<R>) => {
  const rootRef = useRef<HTMLElement | null>(null);
  const dateTimePickerRef = useRef<HTMLElement | null>(null);
  const [openCalendar, setOpenCalendar] = useState(false);
  const [state, setState] = useState<Dayjs | null>(
    value ?? defaultValue ?? null
  );
  const forceRerender = useForceRerender();

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useImperativeHandle(
    ref,
    (() => {
      return {
        instance: dateTimePickerRef.current,
        value: state,
        setValue: (v: Dayjs | null) => {
          setState(v);
        },
        reset: () => {
          setState(null);
        },
      };
    }) as unknown as () => R,
    [dateTimePickerRef.current, state]
  );

  useDidMount(function init() {
    dateTimePickerRef.current =
      rootRef.current?.querySelector(".MuiFormControl-root") ?? null;
    forceRerender();
  });

  const onChangeHandler = (
    newValue: Dayjs | null,
    context: PickerChangeHandlerContext<DateTimeValidationError>
  ) => {
    if (typeof value === "undefined") {
      setState(newValue);
    }
    onChange?.(newValue, context);
  };

  const onCalendarCloseHandler = () => {
    setOpenCalendar(false);
  };

  const toggleCalendar = (event?: React.PointerEvent) => {
    event?.stopPropagation();
    setOpenCalendar((prev) => !prev);
  };

  const valueState = useMemo(
    () => (typeof value !== "undefined" ? value : state),
    [value, state]
  );

  return {
    rootRef,
    dateTimePickerRef,
    valueState,
    openCalendar,
    onChangeHandler,
    onCalendarCloseHandler,
    toggleCalendar,
  };
};
